/**
 * Public pricing page. Pulls live plan catalogue from /api/public/plans
 * and the editable intro markdown from /api/public/legal/pricing.
 * Each plan card has a Subscribe CTA → /checkout?plan=<id>.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Check } from "lucide-react";
import { API } from "@/lib/api";
import { renderMarkdown } from "@/lib/markdown";
import PublicLayout from "./PublicLayout";

const CYCLE_LABEL = { monthly: "/mo", quarterly: "/qtr", annual: "/yr", yearly: "/yr" };
// Legacy /api/public/plans doesn't expose `code` so we map by name to deep-link
// users straight into the locked Subscribe Now flow.
const PLAN_CODE_BY_NAME = {
    "Starter": "starter", "Founding Member": "founder", "Single Advocate": "single",
    "Chambers": "chambers", "Firm Starter": "firm_starter", "Firm Pro": "firm_pro",
};

export default function PricingPage() {
    const [plans, setPlans] = useState([]);
    const [intro, setIntro] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let active = true;
        Promise.all([
            axios.get(`${API}/public/plans`),
            axios.get(`${API}/public/legal/pricing`),
        ]).then(([p, i]) => {
            if (!active) return;
            const list = [...(p.data?.plans || [])].sort((a, b) => {
                const aEnt = a.name === "Enterprise";
                const bEnt = b.name === "Enterprise";
                if (aEnt && !bEnt) return 1;
                if (bEnt && !aEnt) return -1;
                return (a.price || 0) - (b.price || 0);
            });
            setPlans(list);
            setIntro(i.data);
        }).catch(e => active && setErr(e?.response?.data?.detail || "Failed to load pricing"));
        return () => { active = false; };
    }, []);

    return (
        <PublicLayout>
            <div data-testid="pricing-page">
                {intro && (
                    <header className="mb-10 max-w-3xl">
                        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-foreground">{intro.title}</h1>
                        <div className="mt-3 text-muted-foreground leading-relaxed">{renderMarkdown(intro.body_md)}</div>
                    </header>
                )}
                {err && <div className="text-rose-600 text-sm bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="pricing-error">{err}</div>}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pricing-grid">
                    {plans.map(p => (
                        <div key={p.id} className="bg-card border border-border rounded-2xl p-6 flex flex-col gap-3 hover:border-primary/40 hover:shadow-lawos-md transition-all" data-testid={`plan-card-${p.id}`}>
                            <div>
                                <h2 className="font-heading text-lg font-semibold text-foreground">{p.name}</h2>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">{p.description}</p>
                            </div>
                            <div className="flex items-baseline gap-1">
                                {p.name === "Enterprise" ? (
                                    <span className="font-heading text-3xl font-bold text-primary" data-testid={`plan-price-${p.id}`}>Custom</span>
                                ) : !p.price ? (
                                    <>
                                        <span className="font-heading text-3xl font-bold text-primary" data-testid={`plan-price-${p.id}`}>Free</span>
                                        <span className="text-xs text-muted-foreground">forever</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="font-heading text-3xl font-bold text-primary" data-testid={`plan-price-${p.id}`}>₹{p.price.toLocaleString("en-IN")}</span>
                                        <span className="text-xs text-muted-foreground">{CYCLE_LABEL[p.billing_cycle] || `/${p.billing_cycle}`}</span>
                                    </>
                                )}
                            </div>
                            <ul className="space-y-1.5 text-xs text-foreground/85 flex-1">
                                {(p.features || []).map((f, idx) => (
                                    <li key={idx} className="flex gap-1.5">
                                        <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2}/>
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link to={p.name === "Enterprise" ? "/legal/contact" : (PLAN_CODE_BY_NAME[p.name] ? `/register?plan=${PLAN_CODE_BY_NAME[p.name]}` : !p.price ? "/register?plan=starter" : `/register`)} data-testid={`plan-subscribe-${p.id}`}
                                className="block text-center bg-primary text-primary-foreground hover:opacity-90 px-4 py-2.5 rounded-lg text-xs uppercase tracking-[0.16em] font-medium shadow-lawos-sm transition-opacity">
                                {p.name === "Enterprise" ? "Talk to sales" : !p.price ? "Start free" : "Subscribe"}
                            </Link>
                        </div>
                    ))}
                </div>
                {plans.length === 0 && !err && <div className="text-muted-foreground text-sm py-10 text-center">Loading plans…</div>}
            </div>
        </PublicLayout>
    );
}
